import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import api from "../lib/api";

const REFRESH_MS = 60000;

function timeAgo(iso) {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} h ago`;
  return `${Math.round(minutes / (60 * 24))} d ago`;
}

// The in-app notification list: the same booking, payment and reminder
// notices that go out by email and push.
export default function NotificationBell({ user, className = "" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(() => {
    api
      .get("/notifications")
      .then(({ data }) => {
        setItems(data.notifications?.data || []);
        setUnread(data.unread_count || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      api.post("/notifications/read-all").then(() => setUnread(0)).catch(() => {});
    }
  };

  // Booking notices lead to where the booking is managed.
  const target = user.role === "business_owner" ? "/business/calendar" : user.role === "admin" ? "/admin" : "/account/appointments";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        className={`relative flex h-9 w-9 items-center justify-center rounded-full ${className}`}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 max-h-[70vh] w-80 max-w-[90vw] overflow-y-auto rounded-2xl border border-border bg-white text-left shadow-lg">
          <p className="border-b border-border px-4 py-3 text-sm font-semibold text-slate-900">Notifications</p>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">Nothing yet. Booking updates will show up here.</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(target);
                }}
                className={`block w-full border-b border-border px-4 py-3 text-left last:border-b-0 hover:bg-muted ${n.is_read ? "" : "bg-primary/5"}`}
              >
                <p className="text-sm font-medium text-slate-900">{n.title}</p>
                {n.body && <p className="mt-0.5 text-xs text-slate-600">{n.body}</p>}
                <p className="mt-1 text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}